import { z } from 'zod'
import { planStopDtoSchema } from './plan-stop'

export const planTypeValues = ['plan', 'ping', 'activity_request', 'spontaneous'] as const
export const planVisibilityValues = ['private', 'friends', 'group', 'extended', 'public'] as const
export const participantStatusValues = [
  'going',
  'maybe',
  'not_going',
  'en_route',
  'waitlisted',
] as const
export const planCategoryValues = [
  'fitness',
  'going_out',
  'cafe',
  'studying',
  'sports',
  'chill',
  'food',
  'party',
  'date',
] as const
export const planRecurrenceValues = ['weekly', 'biweekly', 'monthly'] as const
/** A plan with a start time but no end counts as happening for this long after the start. */
export const PLAN_LIVE_WITHOUT_END_MINUTES = 120
/** Why a plan that found nobody asks its organizer to make it public or delete it. */
export const organizerPromptValues = ['all_declined', 'nobody_yet'] as const
export type OrganizerPrompt = (typeof organizerPromptValues)[number]

export const planChatPhaseValues = ['open', 'ended', 'archived'] as const
export type PlanChatPhase = (typeof planChatPhaseValues)[number]

const createPlanBaseSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  date: z.string().date().nullable().optional(),
  timeStart: z.string().time().nullable().optional(),
  timeEnd: z.string().time().nullable().optional(),
  /** The whole day: start and end times are cleared. */
  allDay: z.boolean().default(false),
  locationName: z.string().max(200).nullable().optional(),
  timezone: z.string().max(50).nullable().optional(),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  category: z.enum(planCategoryValues).nullable().optional(),
  subcategory: z.string().max(50).nullable().optional(),
  visibility: z.enum(planVisibilityValues),
  groupId: z.string().uuid().nullable().optional(),
  maxParticipants: z.number().int().positive().nullable().optional(),
  requireApproval: z.boolean().default(false),
  allowAllEdit: z.boolean().default(true),
  joinableWhileLive: z.boolean().default(false),
  assistantEnabled: z.boolean().default(true),
  planType: z.enum(planTypeValues).default('plan'),
  recurrence: z.enum(planRecurrenceValues).nullable().optional(),
  invitedUserIds: z.array(z.string().uuid()).max(50).optional(),
  sourceConversationId: z.string().uuid().nullable().optional(),
  sourcePlaceId: z.string().uuid().nullable().optional(),
  linkedEventId: z.string().uuid().nullable().optional(),
  linkedListingId: z.string().uuid().nullable().optional(),
  linkedPlaceId: z.string().uuid().nullable().optional(),
})

export const createPlanSchema = createPlanBaseSchema
  .extend({
    /** The client lists a plan chat only once it is visible; without it the chat starts visible. */
    quietChat: z.boolean().optional(),
    /** The message the plan was typed as, posted to its chat right after; its day and time count. */
    firstMessage: z.string().max(2000).optional(),
  })
  .refine((data) => data.visibility !== 'group' || data.groupId != null, {
    message: 'groupId is required when visibility is group',
    path: ['groupId'],
  })
  .refine((data) => !data.timeEnd || !data.timeStart || data.timeEnd !== data.timeStart, {
    // end < start is a valid overnight plan (16:00 → 00:30); only equal is invalid.
    message: 'timeEnd must differ from timeStart',
    path: ['timeEnd'],
  })

export type CreatePlanInput = z.infer<typeof createPlanSchema>

// Redeclare the defaulted booleans without defaults: zod still applies
// .default() through .partial(), which would inject requireApproval/allowAllEdit
// into every PATCH body — 403ing non-creator editors and resetting the flags.
// planType on update is limited to plan|spontaneous (not ping / activity_request).
export const updatePlanSchema = createPlanBaseSchema
  .omit({ invitedUserIds: true, planType: true, sourceConversationId: true, sourcePlaceId: true })
  .extend({
    requireApproval: z.boolean(),
    allowAllEdit: z.boolean(),
    joinableWhileLive: z.boolean(),
    assistantEnabled: z.boolean(),
    allDay: z.boolean(),
    planType: z.enum(['plan', 'spontaneous']),
  })
  .partial()

export type UpdatePlanInput = z.infer<typeof updatePlanSchema>

export const updateParticipantStatusSchema = z.object({
  status: z.enum(['going', 'maybe', 'not_going', 'en_route']),
})

export type UpdateParticipantStatusInput = z.infer<typeof updateParticipantStatusSchema>

export const joinPlanSchema = z.object({
  status: z.enum(['going', 'maybe']).optional(),
})

export type JoinPlanInput = z.infer<typeof joinPlanSchema>

export const planParticipantDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  status: z.enum(participantStatusValues),
  arrivalTime: z.string().nullable(),
  joinedAt: z.string(),
  /** Viewer's relationship to this participant — drives the inline add-friend affordance. */
  friendship: z.enum(['self', 'friends', 'pending_sent', 'pending_received', 'none']).optional(),
  /** Joined on the web without an account, so never offered as a friend. Absent means unknown. */
  isGuest: z.boolean().optional(),
})

export type PlanParticipantDto = z.infer<typeof planParticipantDtoSchema>

/** Someone invited to a plan who hasn't accepted/declined yet (plan_invites pending). */
export const planInvitedDtoSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

export type PlanInvitedDto = z.infer<typeof planInvitedDtoSchema>

/** A pending request to join an approval-gated plan, awaiting the creator's decision. */
export const planJoinRequestDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  /** Asked to join from the web without an account. Absent means unknown. */
  isGuest: z.boolean().optional(),
})

export type PlanJoinRequestDto = z.infer<typeof planJoinRequestDtoSchema>

export const planDtoSchema = z.object({
  id: z.string(),
  creatorId: z.string(),
  creatorName: z.string().nullable(),
  creatorAvatarUrl: z.string().nullable(),
  planType: z.enum(planTypeValues),
  name: z.string(),
  description: z.string().nullable(),
  date: z.string().nullable(),
  timeStart: z.string().nullable(),
  timeEnd: z.string().nullable(),
  locationName: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Street and city resolved from the coordinate on the server. Empty when the
   * coordinate has no address, or while the backfill has not reached the plan. */
  locationStreet: z.string().optional(),
  locationCity: z.string().optional(),
  timezone: z.string().nullable(),
  category: z.enum(planCategoryValues).nullable(),
  subcategory: z.string().nullable(),
  visibility: z.enum(planVisibilityValues),
  groupId: z.string().nullable(),
  maxParticipants: z.number().nullable(),
  requireApproval: z.boolean(),
  allowAllEdit: z.boolean(),
  assistantEnabled: z.boolean(),
  /** False while nothing about the plan needs a chat; its chat then stays out of Chats. */
  chatVisible: z.boolean(),
  allDay: z.boolean(),
  recurrence: z.enum(planRecurrenceValues).nullable(),
  sourceConversationId: z.string().nullable().optional(),
  sourcePlaceId: z.string().nullable().optional(),
  /** The creator's own cover photo; null means the linked event or listing cover stands in. */
  coverImageUrl: z.string().nullable(),
  participantCount: z.number(),
  activeParticipantCount: z.number(),
  expiredAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type PlanDto = z.infer<typeof planDtoSchema>

export const linkedEventSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  coverImageUrl: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  locationName: z.string().nullable(),
  isCanceled: z.boolean(),
})

export type LinkedEventSummary = z.infer<typeof linkedEventSummarySchema>

export const linkedListingSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  coverImageUrl: z.string().nullable(),
  locationName: z.string().nullable(),
  rating: z.number(),
  /**
   * The venue's number, set only for food-and-drinks listings — the ones you
   * would ring to book a table. Lets a plan offer a call until reservations
   * live on Pulsify.
   */
  reservationPhone: z.string().nullable().optional(),
})

export type LinkedListingSummary = z.infer<typeof linkedListingSummarySchema>

export const linkedPlaceSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  /** Whether the viewer belongs to the spot; only members may open it. */
  isMember: z.boolean(),
})

export type LinkedPlaceSummary = z.infer<typeof linkedPlaceSummarySchema>

export const planDetailDtoSchema = planDtoSchema.extend({
  participants: z.array(planParticipantDtoSchema),
  invited: z.array(planInvitedDtoSchema),
  myStatus: z.enum(participantStatusValues).nullable(),
  chatAvailable: z.boolean(),
  linkedEvent: linkedEventSummarySchema.nullable(),
  linkedListing: linkedListingSummarySchema.nullable(),
  linkedPlace: linkedPlaceSummarySchema.nullable(),
  /** Ordered itinerary; stop 0 mirrors the linked entity columns. */
  stops: z.array(planStopDtoSchema),
  /** Earliest read time across all other active participants, or null if not everyone has read. */
  othersReadAt: z.string().nullable(),
  disappearingTtlSeconds: z.number().int().nullable(),
  /** The viewer has asked to join and the creator has not decided yet. */
  myJoinRequestPending: z.boolean().optional(),
  /** Pending join requests awaiting a decision — only present for the creator. */
  joinRequests: z.array(planJoinRequestDtoSchema).optional(),
  /** Name of the spot or group chat the plan was born in. */
  originName: z.string().nullable().optional(),
  /**
   * Only for the organizer: the plan found nobody, so ask "make it public or delete it?"
   * until they answer. Null while there is nothing to ask; absent for everyone else.
   */
  organizerPrompt: z.enum(organizerPromptValues).nullable().optional(),
  /**
   * Where the plan's chat stands: open, ended (still listed while people talk), or archived
   * (read-only, listed only in the archive). Absent for viewers outside the plan.
   */
  chatPhase: z.enum(planChatPhaseValues).optional(),
  /** Group chat the plan was continued in, once someone did. */
  continuedGroupId: z.string().nullable().optional(),
  /** Whether the viewer may continue the plan as a group chat; false for plans born in a group. */
  mayContinueAsGroup: z.boolean().optional(),
  /** The event whose Live Room group this plan is; null for every other plan. */
  roomEventId: z.string().nullable().optional(),
})

export type PlanDetailDto = z.infer<typeof planDetailDtoSchema>

export const planParticipantPreviewSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

export type PlanParticipantPreview = z.infer<typeof planParticipantPreviewSchema>

export const planFeedItemDtoSchema = planDtoSchema.extend({
  participantAvatars: z.array(z.string().nullable()).max(3),
  /** Earliest participants with names for initials fallbacks (creator first). */
  participantPreviews: z.array(planParticipantPreviewSchema).max(3),
  myStatus: z.enum(participantStatusValues).nullable(),
  distance: z.number().nullable(),
  pollCount: z.number().int().nonnegative(),
  messageCount: z.number().int().nonnegative(),
  listItemCount: z.number().int().nonnegative(),
  pendingInvite: z.boolean(),
  lastMessage: z
    .object({
      preview: z.string().nullable(),
      senderName: z.string().nullable(),
      kind: z.string().nullable(),
      createdAt: z.string(),
    })
    .nullable(),
  unreadCount: z.number().int().nonnegative(),
  muted: z.boolean(),
  /** Name of the spot or group chat the plan was born in, for "at <spot>" / "in <group>" chips. */
  originName: z.string().nullable().optional(),
  /** Spot the plan is linked to, so the card can surface it above the plain location line. */
  linkedPlaceId: z.string().nullable().optional(),
  linkedPlaceName: z.string().nullable().optional(),
  /** Cover of the linked event, else the linked listing; stands in when the plan has no cover of its own. */
  linkedCoverUrl: z.string().nullable().optional(),
  /** Over and quiet: listed only in the plans archive, its chat read-only. Set by the chat lists. */
  archived: z.boolean().optional(),
})

export type PlanFeedItemDto = z.infer<typeof planFeedItemDtoSchema>

export const eventPlansDtoSchema = z.object({
  totalGoing: z.number().int().nonnegative(),
  planCount: z.number().int().nonnegative(),
  plans: z.array(planFeedItemDtoSchema),
})

export type EventPlansDto = z.infer<typeof eventPlansDtoSchema>

export const planResponseSchema = z.object({
  plan: planDtoSchema,
})

export const planDetailResponseSchema = z.object({
  plan: planDetailDtoSchema,
})

export const planFeedResponseSchema = z.object({
  pinned: z.array(planFeedItemDtoSchema),
  feed: z.array(planFeedItemDtoSchema),
  nextCursor: z.string().nullable(),
})

export const planChatsResponseSchema = z.object({
  plans: z.array(planFeedItemDtoSchema),
})

export type PlanChatsResponse = z.infer<typeof planChatsResponseSchema>

export const planChatArchiveResponseSchema = z.object({
  plans: z.array(planFeedItemDtoSchema),
  /** How many archived plan chats there are in all; set on the first page only. */
  total: z.number().int().nonnegative().nullable(),
  nextCursor: z.string().nullable(),
})

export type PlanChatArchiveResponse = z.infer<typeof planChatArchiveResponseSchema>

export const continuePlanAsGroupResponseSchema = z.object({
  groupId: z.string(),
})

export type ContinuePlanAsGroupResponse = z.infer<typeof continuePlanAsGroupResponseSchema>

export const planParticipantResponseSchema = z.object({
  participant: planParticipantDtoSchema,
})

export const planSearchResponseSchema = z.object({
  results: z.array(z.record(z.string(), z.unknown())),
})

export const invitedResponseSchema = z.object({
  invited: z.number().int().nonnegative(),
  invitedGroups: z.number().int().nonnegative(),
  /** Selections that reached nobody — blocked, no longer a friend, or gone. */
  skipped: z.number().int().nonnegative(),
  /** Whether the plan chat is visible after these invites. */
  chatVisible: z.boolean(),
})

export const planFeedQuerySchema = z.object({
  sort: z.enum(['smart', 'nearby', 'newest', 'popular']).optional().default('smart'),
  cursor: z.string().optional(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
})

export const planRecallQuerySchema = z.object({
  q: z.string().trim().min(3).max(80),
})

/** The viewer's latest plan matching what they are typing: who was in, and where it was. */
export const planRecallSchema = z.object({
  planId: z.string().uuid(),
  name: z.string(),
  people: z.array(planParticipantPreviewSchema),
  place: z
    .object({
      listingId: z.string().uuid(),
      name: z.string(),
      locationName: z.string().nullable(),
      coverImageUrl: z.string().nullable(),
      lat: z.number().nullable(),
      lng: z.number().nullable(),
    })
    .nullable(),
})

export type PlanRecall = z.infer<typeof planRecallSchema>

export const planRecallResponseSchema = z.object({ recall: planRecallSchema.nullable() })

export const planSearchQuerySchema = z.object({
  q: z.string().min(1).max(100),
})

export const planFeedDismissRequestSchema = z.object({
  planId: z.string().uuid(),
})

export const planFeedSeenRequestSchema = z.object({
  plans: z
    .array(
      z.object({
        planId: z.string().uuid(),
        tapped: z.boolean().optional().default(false),
      }),
    )
    .min(1)
    .max(100),
})

export const joinRequestActionSchema = z.object({
  action: z.enum(['approve', 'decline']),
})

export const arrivalRequestSchema = z.object({
  arrivalTime: z.string().time().nullable(),
})

export const PLAN_INVITE_MAX_USERS = 50
export const PLAN_INVITE_MAX_GROUPS = 20

export const bulkInviteRequestSchema = z
  .object({
    userIds: z.array(z.string().uuid()).max(PLAN_INVITE_MAX_USERS).default([]),
    /** Group conversations to invite: each gets one card every member answers on. */
    conversationIds: z.array(z.string().uuid()).max(PLAN_INVITE_MAX_GROUPS).default([]),
  })
  .refine((value) => value.userIds.length + value.conversationIds.length > 0, {
    message: 'Select at least one friend or group',
  })

export const planShareTokenResponseSchema = z.object({
  url: z.string(),
  token: z.string().nullable(),
  expiresAt: z.string().nullable(),
})

export type PlanShareTokenResponse = z.infer<typeof planShareTokenResponseSchema>

export const redeemPlanShareRequestSchema = z.object({
  token: z.string().min(1),
})

export type RedeemPlanShareInput = z.infer<typeof redeemPlanShareRequestSchema>

export const PLAN_INVITE_PREVIEW_MAX_FACES = 5

export const planInvitePreviewStatusValues = ['valid', 'expired', 'invalid', 'unknown'] as const
export type PlanInvitePreviewStatus = (typeof planInvitePreviewStatusValues)[number]

/**
 * Pre-authentication view of a plan behind a share token. Only `valid` carries
 * `plan`; every other status deliberately carries nothing, so a tampered or
 * expired token reveals no plan at all. Nobody going is named: `goingFaces` carries
 * only the pictures of the first people who are in, organizer first, for the preview clip.
 */
export const planInvitePreviewResponseSchema = z.object({
  status: z.enum(planInvitePreviewStatusValues),
  plan: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      date: z.string().nullable(),
      timeStart: z.string().nullable(),
      timeEnd: z.string().nullable().optional(),
      allDay: z.boolean().optional(),
      locationName: z.string().nullable(),
      goingCount: z.number().int(),
      requireApproval: z.boolean(),
      /** A null picture is someone without one, or not confirmed 16+: the clip draws a stand-in. */
      goingFaces: z
        .array(z.object({ avatarUrl: z.string().nullable() }))
        .max(PLAN_INVITE_PREVIEW_MAX_FACES)
        .optional(),
    })
    .optional(),
  invitedBy: z
    .object({
      id: z.string().uuid(),
      displayName: z.string().nullable(),
      avatarUrl: z.string().nullable(),
    })
    .nullable()
    .optional(),
})
export type PlanInvitePreviewResponse = z.infer<typeof planInvitePreviewResponseSchema>

export const planChatUnreadResponseSchema = z.object({
  planIds: z.array(z.string().uuid()),
})

export type PlanChatUnreadResponse = z.infer<typeof planChatUnreadResponseSchema>

export type PlanResponse = z.infer<typeof planResponseSchema>
export type PlanDetailResponse = z.infer<typeof planDetailResponseSchema>
export type PlanFeedResponse = z.infer<typeof planFeedResponseSchema>
export type PlanParticipantResponse = z.infer<typeof planParticipantResponseSchema>
export type InvitedResponse = z.infer<typeof invitedResponseSchema>

export const setActivityStatusSchema = z.object({
  text: z.string().min(1).max(80),
  emoji: z.string().max(16).nullable().optional(),
  expiresAt: z.string().datetime(),
})

export type SetActivityStatusInput = z.infer<typeof setActivityStatusSchema>

/** A friend's lightweight "doing X right now" broadcast — a stealth activity_request plan that becomes a real plan when someone joins. */
export const activityStatusDtoSchema = z.object({
  id: z.string(),
  creatorId: z.string(),
  creatorName: z.string().nullable(),
  creatorAvatarUrl: z.string().nullable(),
  text: z.string(),
  emoji: z.string().nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
})

export type ActivityStatusDto = z.infer<typeof activityStatusDtoSchema>

export const activityStatusesResponseSchema = z.object({
  mine: activityStatusDtoSchema.nullable(),
  friends: z.array(activityStatusDtoSchema),
})

export type ActivityStatusesResponse = z.infer<typeof activityStatusesResponseSchema>

export const activityStatusResponseSchema = z.object({
  status: activityStatusDtoSchema,
})

export type ActivityStatusResponse = z.infer<typeof activityStatusResponseSchema>

export const livePlanParticipantSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  status: z.string(),
})

export const livePlanDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  locationName: z.string().nullable(),
  /** Plan day; a time alone cannot say whether "20:00" is tonight or tomorrow. */
  date: z.string().nullable(),
  timeStart: z.string().nullable(),
  /* Optional across the board: a client may be newer than the backend it talks
     to, and a required field would fail the whole parse instead of one card. */
  timeEnd: z.string().nullable().optional(),
  allDay: z.boolean().optional(),
  creatorId: z.string().optional(),
  creatorName: z.string().nullable().optional(),
  creatorAvatarUrl: z.string().nullable().optional(),
  linkedListingId: z.string().nullable(),
  linkedEventId: z.string().nullable(),
  linkedPlaceId: z.string().nullable(),
  linkedPlaceName: z.string().nullable(),
  joinableWhileLive: z.boolean(),
  /** The requesting user's own participation status, null if not a participant. */
  myStatus: z.string().nullable(),
  goingCount: z.number().int(),
  enRouteCount: z.number().int(),
  participants: z.array(livePlanParticipantSchema),
})

export const livePlansResponseSchema = z.object({ plans: z.array(livePlanDtoSchema) })

export type LivePlanDto = z.infer<typeof livePlanDtoSchema>
export type LivePlansResponse = z.infer<typeof livePlansResponseSchema>
